// The Apogee formula (docs/02-Game-Design/Scoring.md). Integer arithmetic
// throughout; the shape bonus rounds half up so browser and Worker agree.
import type { PlayLevel } from '../level/schema';
import { nodeMap } from '../play/state';
import { treeLines } from '../play/tree';
import type { Constellation } from '../play/types';
import { letterValue } from './letterValues';
import { shapeBonusPercent, shapeMetrics, type ShapeMetrics } from './shape';

export interface WordScore {
  word: string;
  /** Σ letter values, the stars round a ringed planet counting double, before the shape bonus. */
  letters: number;
  shape: ShapeMetrics;
  shapeBonusPercent: number;
  /** The constellation ringed a planet. */
  planet: boolean;
  total: number;
}

export function scoreConstellation(level: PlayLevel, c: Constellation): WordScore {
  const nodes = nodeMap(level);
  // The stars on the loop round a ringed planet are worth double.
  const loop = new Set(c.planet?.loop ?? []);
  let letters = 0;
  for (const id of c.nodes) {
    const node = nodes.get(id);
    if (!node) continue;
    letters += letterValue(level.lang, node.letter) * (loop.has(id) ? 2 : 1);
  }
  // The shape is the tapped tree's; the closing line is not part of it.
  const shape = shapeMetrics(c.nodes, treeLines(c));
  const bonus = shapeBonusPercent(shape.value);
  const total = Math.floor((letters * (100 + bonus) + 50) / 100);
  return { word: c.word, letters, shape, shapeBonusPercent: bonus, planet: c.planet !== undefined, total };
}

export interface PlayScore {
  words: WordScore[];
  /** Σ word scores: the score during play, and the level score once finished. */
  running: number;
}

export function scorePlay(level: PlayLevel, constellations: readonly Constellation[]): PlayScore {
  const words = constellations.map((c) => scoreConstellation(level, c));
  return { words, running: words.reduce((sum, w) => sum + w.total, 0) };
}
